import { assign, setup } from 'xstate';
import type { ReceivingSession, ReceivingSessionState } from '../data/receiving';
import { receivingLog } from '../lib/receiving-log';

type ReceivingContext = {
  storeId: string;
  session: ReceivingSession | null;
  // Monotonic count of refused Resume probes seen this session. The pause screen
  // watches it for increments to surface "still in use" on every refusal — a
  // boolean couldn't distinguish back-to-back refusals (probe_refused stays true
  // across them, so no false→true edge would fire on the second click).
  probeRefusedCount: number;
  // Abandon-side analogue: count of busy-refused Abandon clears (the companion
  // replied rcv_clear_result{busy:true} — LPOS holds the file, session kept
  // alive). Screens watch it to show "close LiquorPOS, then Abandon again".
  abandonBusyCount: number;
};

type ReceivingInput = {
  storeId: string;
  initialSession?: ReceivingSession;
};

type ReceivingEvent = {
  type: 'SESSION_UPDATED';
  session: ReceivingSession;
  probeRefused?: boolean;
  abandonBusy?: boolean;
};

// From each state, which states may it transition to via SESSION_UPDATED?
// `cancelled` and `complete` are terminals — no outbound transitions.
// `failed` can rewind to any state (retry paths vary: sending_to_lpos→review, processing→get_invoices).
const legalTransitions: Record<
  ReceivingSessionState | 'idle' | 'starting',
  readonly ReceivingSessionState[]
> = {
  idle: [
    'selecting_companion',
    'selecting_vendor',
    'get_invoices',
    'processing',
    'review',
    'sending_to_lpos',
    'paused_lpos_busy',
    'waiting_for_lpos',
    'complete',
    'failed',
    'cancelled',
  ],
  starting: ['selecting_companion', 'selecting_vendor', 'failed', 'cancelled'],
  selecting_companion: ['selecting_vendor', 'failed', 'cancelled'],
  selecting_vendor: ['get_invoices', 'paused_lpos_busy', 'failed', 'cancelled'],
  get_invoices: ['processing', 'paused_lpos_busy', 'failed', 'cancelled'],
  processing: ['review', 'paused_lpos_busy', 'failed', 'cancelled'],
  review: ['sending_to_lpos', 'paused_lpos_busy', 'failed', 'cancelled'],
  sending_to_lpos: ['waiting_for_lpos', 'paused_lpos_busy', 'failed'],
  paused_lpos_busy: [
    // Resume resolves back to paused_from (any pre-Confirm state or sending_to_lpos)
    'selecting_companion',
    'selecting_vendor',
    'get_invoices',
    'processing',
    'review',
    'sending_to_lpos',
    'waiting_for_lpos',
    'failed',
    'cancelled',
  ],
  waiting_for_lpos: ['complete', 'failed', 'cancelled'],
  complete: [],
  failed: [
    'selecting_companion',
    'selecting_vendor',
    'get_invoices',
    'review',
    'sending_to_lpos',
    'processing',
    'failed',
    'cancelled',
  ],
  cancelled: [],
};

const isLegalTransition = (
  from: ReceivingSessionState | 'idle' | 'starting',
  to: ReceivingSessionState
): boolean => {
  if (from === to) {
    return true; // re-emissions allowed
  }

  return (legalTransitions[from] as readonly string[]).includes(to);
};

export const receivingMachine = setup({
  types: {} as {
    context: ReceivingContext;
    events: ReceivingEvent;
    input: ReceivingInput;
  },
  actions: {
    applySession: assign({
      session: ({ event }) => (event.type === 'SESSION_UPDATED' ? event.session : null),
      // Increment on every refused-probe update so the UI can react to each
      // refusal, not just the first (probe_refused stays true across back-to-back
      // refusals, so a boolean would only ever edge once).
      probeRefusedCount: ({ context, event }) =>
        event.type === 'SESSION_UPDATED' && event.probeRefused
          ? context.probeRefusedCount + 1
          : context.probeRefusedCount,
      // Same counting rationale for busy-refused Abandon clears.
      abandonBusyCount: ({ context, event }) =>
        event.type === 'SESSION_UPDATED' && event.abandonBusy
          ? context.abandonBusyCount + 1
          : context.abandonBusyCount,
    }),
  },
}).createMachine({
  id: 'receiving',
  context: ({ input }) => ({
    storeId: input.storeId,
    session: input.initialSession ?? null,
    probeRefusedCount: 0,
    abandonBusyCount: 0,
  }),
  initial: 'hydrating',
  // Root fallback. xstate bubbles an event to the root only when no active
  // state took it — i.e. exactly the SESSION_UPDATED pushes the per-state
  // guards rejected. Without this the drop is completely invisible (no state
  // change, no action, no log), so a backend/frontend transition-table drift
  // reaches us as "the page stopped working" with nothing to search for.
  // Logging it turns that class into a greppable line (ORD-367).
  on: {
    SESSION_UPDATED: {
      actions: ({ context, event }) => {
        const from = context.session?.state ?? 'idle';

        // Terminal states are xstate final states with no `on` handler, so
        // benign redelivered pushes bubble here too. Re-check before logging
        // so a re-emission isn't reported as drift.
        if (isLegalTransition(from, event.session.state)) {
          return;
        }

        receivingLog('illegal_transition', {
          store_id: context.storeId,
          session_id: context.session?.session_id ?? null,
          from_state: from,
          to_state: event.session.state,
          trigger: 'SESSION_UPDATED',
        });
      },
    },
  },
  states: {
    // Transient: immediately evaluates `always` guards to pick the correct initial state.
    hydrating: {
      always: [
        { target: 'idle', guard: ({ context }) => context.session === null },
        {
          target: 'selecting_companion',
          guard: ({ context }) => context.session?.state === 'selecting_companion',
        },
        {
          target: 'selecting_vendor',
          guard: ({ context }) => context.session?.state === 'selecting_vendor',
        },
        {
          target: 'get_invoices',
          guard: ({ context }) => context.session?.state === 'get_invoices',
        },
        {
          target: 'processing',
          guard: ({ context }) => context.session?.state === 'processing',
        },
        {
          target: 'review',
          guard: ({ context }) => context.session?.state === 'review',
        },
        {
          target: 'sending_to_lpos',
          guard: ({ context }) => context.session?.state === 'sending_to_lpos',
        },
        {
          target: 'paused_lpos_busy',
          guard: ({ context }) => context.session?.state === 'paused_lpos_busy',
        },
        {
          target: 'waiting_for_lpos',
          guard: ({ context }) => context.session?.state === 'waiting_for_lpos',
        },
        {
          target: 'complete',
          guard: ({ context }) => context.session?.state === 'complete',
        },
        {
          target: 'failed',
          guard: ({ context }) => context.session?.state === 'failed',
        },
        {
          target: 'cancelled',
          guard: ({ context }) => context.session?.state === 'cancelled',
        },
        { target: 'idle' }, // fallback
      ],
    },

    idle: {
      on: {
        SESSION_UPDATED: [
          {
            guard: ({ event }) => isLegalTransition('idle', event.session.state),
            target: 'hydrating',
            actions: 'applySession',
          },
        ],
      },
    },

    selecting_companion: {
      on: {
        SESSION_UPDATED: [
          {
            guard: ({ event }) => isLegalTransition('selecting_companion', event.session.state),
            target: 'hydrating',
            actions: 'applySession',
          },
        ],
      },
    },

    starting: {
      on: {
        SESSION_UPDATED: [
          {
            guard: ({ event }) => isLegalTransition('starting', event.session.state),
            target: 'hydrating',
            actions: 'applySession',
          },
        ],
      },
    },

    selecting_vendor: {
      on: {
        SESSION_UPDATED: [
          {
            guard: ({ event }) => isLegalTransition('selecting_vendor', event.session.state),
            target: 'hydrating',
            actions: 'applySession',
          },
        ],
      },
    },

    get_invoices: {
      on: {
        SESSION_UPDATED: [
          {
            guard: ({ event }) => isLegalTransition('get_invoices', event.session.state),
            target: 'hydrating',
            actions: 'applySession',
          },
        ],
      },
    },

    processing: {
      on: {
        SESSION_UPDATED: [
          {
            guard: ({ event }) => isLegalTransition('processing', event.session.state),
            target: 'hydrating',
            actions: 'applySession',
          },
        ],
      },
    },

    review: {
      on: {
        SESSION_UPDATED: [
          {
            guard: ({ event }) => isLegalTransition('review', event.session.state),
            target: 'hydrating',
            actions: 'applySession',
          },
        ],
      },
    },

    sending_to_lpos: {
      on: {
        SESSION_UPDATED: [
          {
            guard: ({ event }) => isLegalTransition('sending_to_lpos', event.session.state),
            target: 'hydrating',
            actions: 'applySession',
          },
        ],
      },
    },

    paused_lpos_busy: {
      on: {
        SESSION_UPDATED: [
          {
            guard: ({ event }) => isLegalTransition('paused_lpos_busy', event.session.state),
            target: 'hydrating',
            actions: 'applySession',
          },
        ],
      },
    },

    waiting_for_lpos: {
      on: {
        SESSION_UPDATED: [
          {
            guard: ({ event }) => isLegalTransition('waiting_for_lpos', event.session.state),
            target: 'hydrating',
            actions: 'applySession',
          },
        ],
      },
    },

    complete: { type: 'final' },

    failed: {
      on: {
        SESSION_UPDATED: [
          {
            guard: ({ event }) => isLegalTransition('failed', event.session.state),
            target: 'hydrating',
            actions: 'applySession',
          },
        ],
      },
    },

    cancelled: { type: 'final' },
  },
});
